<template>
  <v-flex xs12>
    <data-source-table
      itemController="actionScripts"
      itemIdentifyField="name"
      :headers="headers"
      :options.sync="options"
      :selected-items.sync="selectedItems"
      ref="dataSourceRef"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <confirm-dialog
            :is-open.sync="dialogs.newScript.isOpened"
            width="400"
            @confirm="saveNew"
            confirm-button-text="save"
            :is-disable-confirm-button="!isNewScriptHasEnoughData"
          >
            <template v-slot:activator>
              <v-btn
                class="mx-2" color="green"
                dark fab small
                @click="openDialog('newScript')"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" dark>
                      mdi-plus
                    </v-icon>
                  </template>
                  Add new
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:title>
              Add new script
            </template>
            <template v-slot:body>
              <v-container>
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="dialogs.newScript.data.name"
                      label="name"
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="dialogs.newScript.data.map_res_id"
                      :items="mapResolutionList"
                      :filter="nonDiacriticsSensitiveFilter"
                      item-text="name"
                      item-value="id"
                      label="map resolution"
                      clearable
                      dense
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="dialogs.newScript.data.mode_id"
                      :items="modesList"
                      :filter="nonDiacriticsSensitiveFilter"
                      item-text="name"
                      item-value="id"
                      label="mode"
                      dense
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="dialogs.newScript.data.type_id"
                      :items="typesList"
                      :filter="nonDiacriticsSensitiveFilter"
                      item-text="name"
                      item-value="id"
                      label="type"
                      clearable
                      dense
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-file-input
                      v-model="dialogs.newScript.data.xml_file"
                      accept=".xml"
                      label="Upload xml"
                      :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row v-if="dialogs.newScript.data.mode_id !== 1">
                  <v-col>
                    <DataList
                      :item-model.sync="dialogs.newScript.data.pois"
                      :type-object="$constants.DATA_LISTS.POI_SELECT_LIST"
                      label="poi"
                      :item-text-function="item => `${item.id}: ${item.name}`"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                    />
                  </v-col>
                </v-row>
                <v-row v-if="dialogs.newScript.data.mode_id !== 1">
                  <v-col>
                    <DataList
                      :item-model.sync="dialogs.newScript.data.geotags"
                      :type-object="$constants.DATA_LISTS.GEOTAG_LIST"
                      label="geotag"
                      :item-text-function="item => `${item.id}: ${item.name}`"
                      clearable
                      multiple
                      dense
                      chips
                      small-chips
                      deletable-chips
                    />
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.bulkDelete.isOpened"
            dialog-max-width="400"
            @confirm="bulkDelete"
            confirm-button-text="delete"
          >
            <template v-slot:activator>
              <v-btn
                class="mx-2" color="red darken-3"
                dark fab small
                @click="openDialog('bulkDelete')"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <slot>
                      <v-icon v-bind="attrs" v-on="on" dark>
                        mdi-delete
                      </v-icon>
                    </slot>
                  </template>
                  bulk delete
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:title>
              Bulk delete
            </template>
            <template v-slot:body>
              Are you sure you want to
              delete this {{ selectedItems.length }}?
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.copydialog.isOpened"
            width="400"
            @confirm="copyScript"
            confirm-button-text="copy"
          >
            <template v-slot:title>
              Copy action script
            </template>
            <template v-slot:body>
              <v-container>
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="dialogs.copydialog.data.name"
                      label="name"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </confirm-dialog>

          <confirm-dialog
            :is-open.sync="dialogs.xmldialog.isOpened"
            width="400"
            @confirm="updateXml"
            confirm-button-text="save"
          >
            <template v-slot:title>
              {{ getDialogData('xmldialog', 'item.name') }}
              {{ `(ID: ${getDialogData('xmldialog', 'item.id')})` }}
            </template>
            <template v-slot:body>
              <v-textarea v-model="dialogs.xmldialog.data.xml"></v-textarea>
            </template>
          </confirm-dialog>

          <v-spacer/>

          <v-btn
              class="mx-2" color="blue darken-1"
              outlined small
              @click="$refs.dataSourceRef ? $refs.dataSourceRef.openDialogSelectColumns() : false"
          >
            Select columns
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.id="{ item }">
        {{ item.id }}
        <v-btn
          class="mx-2" color="grey"
          dark icon small
          @click="openDialog('copydialog', { item: item })"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" dark>
                mdi-content-copy
              </v-icon>
            </template>
            Copy
          </v-tooltip>
        </v-btn>
        <v-btn
          class="mx-2" color="blue"
          dark icon small
          @click="openDialog('xmldialog', { item: item, xml: item.xml })"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" dark>
                mdi-xml
              </v-icon>
            </template>
            Edit xml
          </v-tooltip>
        </v-btn>
        <v-btn
          class="mx-2" color="blue"
          dark icon small
          @click="exportXml(item)"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" dark>
                mdi-file-export
              </v-icon>
            </template>
            Export xml
          </v-tooltip>
        </v-btn>
      </template>

      <template v-slot:item.pois="{ item }">
        <div>
          <EditableList
            v-if="item.mode_id !== 1"
            label="poi"
            :object="item"
            :values="item.pois"
            :list-type-object="$constants.DATA_LISTS.POI_SELECT_LIST"
            description-field="id"
            :multiple="true"
            @addItem="addPoi"
            @removeItem="removePoi"
          />
        </div>
      </template>

      <template v-slot:item.geotags="{ item }">
        <div>
          <EditableList
            v-if="item.mode_id !== 1"
            label="geotag"
            :object="item"
            :values="item.geotags"
            :list-type-object="$constants.DATA_LISTS.GEOTAG_LIST"
            description-field="country_state"
            :multiple="true"
            @addItem="addGeotag"
            @removeItem="removeGeotag"
          />
        </div>
      </template>
    </data-source-table>
  </v-flex>
</template>

<script>

import DataSourceTable from '@/components/DataSource/DataTable.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import DataList from '@/components/DataLists/DataList.vue';
import EditableList from '@/components/EditableList.vue';
import manageDialogsMixin from '../mixins/manageDialogsMixin';

export default {
  name: 'ActionScripts',

  components: {
    EditableList,
    DataList,
    ConfirmDialog,
    DataSourceTable,
  },

  mixins: [
    manageDialogsMixin,
  ],

  data: () => ({
    options: { filters: { } },
    selectedItems: [],
    mapResolutionList: [],
    modesList: [],
    typesList: [],

    requiredRule: [
      (v) => !!v || 'Required',
    ],

    dialogs: {
      newScript: {
        isOpened: false,
        data: {
          name: '',
          map_res_id: '',
          mode_id: '',
          type_id: '',
          xml_file: '',
          pois: [],
          geotags: [],
        },
        defaults: {
          name: '',
          map_res_id: '',
          mode_id: '',
          type_id: '',
          xml_file: '',
          pois: [],
          geotags: [],
        },
      },
      bulkDelete: {
        isOpened: false,
        data: {
        },
        defaults: {
        },
      },
      xmldialog: {
        isOpened: false,
        data: {
          item: {},
          xml: '',
        },
        defaults: {
          item: {},
          xml: '',
        },
      },
      copydialog: {
        isOpened: false,
        data: {
          item: {},
          name: '',
        },
        defaults: {
          item: {},
          name: '',
        },
      },
    },
  }),

  created() {
    this.$requestHelpers
      .get('api/admin/actionScripts/filtersList')
      .then((response) => {
        this.mapResolutionList = response.data.result.mapResolution;
        this.modesList = response.data.result.mode;
        this.typesList = response.data.result.type;
      });
  },

  methods: {
    saveNew() {
      const data = new FormData();
      Object.keys(this.dialogs.newScript.data).forEach((key) => {
        data.append(key, this.dialogs.newScript.data[key]);
      });

      this.$requestHelpers
        .post('api/admin/actionScripts',
          data,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
        .then((response) => {
          if (response.data.success) {
            this.$refs.dataSourceRef.addItem(response.data.result);
          }
        }).finally(() => {
          this.closeDialog('newScript');
        });
    },

    bulkDelete() {
      if (!this.selectedItems.length) {
        this.closeDialog('bulkDelete');
        return false;
      }

      const ids = this.selectedItems.map(
        (item) => item.id,
      );

      this.$refs.dataSourceRef.removeBulk(ids).finally(() => {
        this.closeDialog('bulkDelete');
      });

      return true;
    },

    addPoi(item, poi) {
      if (!poi) return;

      this.$refs.dataSourceRef.updateItemField('add_poi', poi, item);
    },

    removePoi(item, poi) {
      this.$refs.dataSourceRef.updateItemField('remove_poi', poi, item);
    },

    addGeotag(item, geotag) {
      if (!geotag) return;

      this.$refs.dataSourceRef.updateItemField('add_geotag', geotag, item);
    },

    removeGeotag(item, geotag) {
      if (!geotag) return;

      this.$refs.dataSourceRef.updateItemField('remove_geotag', geotag, item);
    },

    updateXml() {
      const xml = this.getDialogData('xmldialog', 'xml');

      this.$refs.dataSourceRef.updateItemField('xml', xml, this.dialogs.xmldialog.data.item)
        .finally(() => this.closeDialog('xmldialog'));
    },

    exportXml(item) {
      const blob = new Blob([item.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.name}.xml`.replace(' ', '_');

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    copyScript() {
      const item = this.getDialogData('copydialog', 'item');
      const name = this.getDialogData('copydialog', 'name');

      const newItem = { ...item, name };

      newItem.pois = newItem.pois.map((poi) => poi.id);
      newItem.geotags = newItem.geotags.map((geotag) => geotag.id);

      this.$refs.dataSourceRef.saveNew(newItem)
        .finally(() => this.closeDialog('copydialog'));
    },
  },

  computed: {
    headers() {
      return [
        {
          text: 'id',
          value: 'id',
          width: '205px',
        },
        {
          text: 'name',
          value: 'name',
          edit: { type: 'text', emptyPlaceholder: true },
        },
        {
          text: 'map resolution',
          value: 'map_res_id',
          filter: { type: 'select', items: this.mapResolutionList },
          edit: {
            type: 'select', items: this.mapResolutionList, itemText: 'name', itemValue: 'id',
          },
        },
        {
          text: 'type',
          value: 'type_id',
          filter: { type: 'select', items: this.typesList },
          edit: {
            type: 'select', items: this.typesList, itemText: 'name', itemValue: 'id',
          },
        },
        {
          text: 'mode',
          value: 'mode_id',
          filter: { type: 'select', items: this.modesList },
          edit: {
            type: 'select', items: this.modesList, itemText: 'name', itemValue: 'id',
          },
        },
        {
          text: 'pois',
          value: 'pois',
          sortable: false,
        },
        {
          text: 'geotags',
          value: 'geotags',
          sortable: false,
        },
        {
          text: '',
          value: 'deleteItem',
          sortable: false,
          width: '80px',
        },
      ];
    },

    isNewScriptHasEnoughData() {
      return this.dialogs.newScript.data.name
        && this.dialogs.newScript.data.map_res_id
        && this.dialogs.newScript.data.mode_id
        && this.dialogs.newScript.data.type_id
        && this.dialogs.newScript.data.xml_file;
    },
  },
};
</script>

<style scoped>

</style>
